import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";

const shopItems = [
  { to: "/shop/candles", label: "Scented Candles", id: "nav-candles", note: "Hand-poured jars" },
  { to: "/shop/planters", label: "Floral Planters", id: "nav-planters", note: "Custom arrangements" },
  { to: "/shop/oils", label: "Hair Growth Oils", id: "nav-oils", note: "Nourish · Strengthen" },
  { to: "/shop/body-butter", label: "Body Butter", id: "nav-body-butter", note: "Whipped shea + coconut" },
  { to: "/shop/body-oil", label: "Body Oil", id: "nav-body-oil", note: "Silk finish · scented" },
  { to: "/shop/soaps", label: "Handmade Soaps", id: "nav-soaps", note: "3 for $27" },
  { to: "/shop/tshirts", label: "Signature T-Shirts", id: "nav-tshirts", note: "Hand spray-painted" },
  { to: "/shop/drinkware", label: "Drinkware", id: "nav-drinkware", note: "Mugs · Bottles · Tumblers" },
];

const topItems = [
  { to: "/membership", label: "Membership", id: "nav-membership" },
  { to: "/connect", label: "Let's Connect", id: "nav-connect" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const shopRef = useRef(null);

  const shopActive = location.pathname.startsWith("/shop/");

  // Close shop dropdown on outside click or route change
  useEffect(() => {
    const handler = (e) => {
      if (shopRef.current && !shopRef.current.contains(e.target)) setShopOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setShopOpen(false);
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-brand-cream/85 backdrop-blur-md border-b border-brand-moss/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/logo_hires.png"
            alt="414, LLC"
            className="w-16 h-16 object-contain transition-transform duration-300 group-hover:rotate-6 group-hover:scale-105"
          />
          <span className="leading-none">
            <span className="block font-serif text-2xl tracking-tight text-brand-moss">
              414, <span className="text-brand-gold italic">LLC</span>
            </span>
            <span className="block text-[10px] uppercase tracking-[0.35em] text-brand-moss/60 mt-1">
              Rooted in Faith · Growing with Purpose
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {/* SHOP DROPDOWN */}
          <div ref={shopRef} className="relative">
            <button
              type="button"
              data-testid="nav-shop-trigger"
              onClick={() => setShopOpen((v) => !v)}
              className={`relative inline-flex items-center gap-1 text-sm tracking-wide transition-colors duration-200 ${
                shopActive || shopOpen
                  ? "text-brand-moss"
                  : "text-brand-moss/70 hover:text-brand-moss"
              }`}
              aria-haspopup="menu"
              aria-expanded={shopOpen}
            >
              Shop
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  shopOpen ? "rotate-180" : ""
                }`}
              />
              {shopActive && (
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-brand-gold" />
              )}
            </button>

            {shopOpen && (
              <div
                data-testid="shop-dropdown"
                role="menu"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-80 bg-brand-cream border border-brand-moss/10 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
              >
                <div className="p-2">
                  {shopItems.map((item) => (
                    <NavLink
                      key={item.id}
                      to={item.to}
                      data-testid={item.id}
                      role="menuitem"
                      className={({ isActive }) =>
                        `flex items-start gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isActive
                            ? "bg-brand-moss/10 text-brand-moss"
                            : "hover:bg-brand-moss/5 text-brand-moss/80 hover:text-brand-moss"
                        }`
                      }
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-gold shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-serif text-base leading-tight">
                          {item.label}
                        </span>
                        <span className="block text-[10px] uppercase tracking-[0.28em] text-brand-moss/50 mt-1">
                          {item.note}
                        </span>
                      </span>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>

          {topItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              data-testid={item.id}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors duration-200 ${
                  isActive
                    ? "text-brand-moss"
                    : "text-brand-moss/70 hover:text-brand-moss"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-brand-gold" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {user ? (
            <>
              {user.role === "admin" && (
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-brand-gold text-brand-moss"
                        : "border border-brand-gold/60 text-brand-gold hover:bg-brand-gold/10"
                    }`
                  }
                >
                  Admin
                </NavLink>
              )}
              <NavLink
                to="/dashboard"
                data-testid="nav-dashboard"
                className="inline-flex items-center gap-2 bg-brand-moss text-brand-gold hover:bg-brand-moss-light rounded-full px-4 py-2 text-sm transition-colors"
              >
                <User className="w-4 h-4" /> Dashboard
              </NavLink>
            </>
          ) : (
            <NavLink
              to="/login"
              data-testid="nav-login"
              className="inline-flex items-center gap-2 border border-brand-moss/25 text-brand-moss hover:bg-brand-moss/5 rounded-full px-4 py-2 text-sm transition-colors"
            >
              <User className="w-4 h-4" /> Sign in
            </NavLink>
          )}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-brand-moss"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-brand-moss/10 bg-brand-cream">
          <div className="px-6 py-4 flex flex-col">
            <button
              type="button"
              data-testid="m-nav-shop-trigger"
              onClick={() => setMobileShopOpen((v) => !v)}
              className="flex items-center justify-between py-3 text-brand-moss/80 hover:text-brand-moss text-base"
              aria-expanded={mobileShopOpen}
            >
              Shop
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobileShopOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileShopOpen && (
              <div className="pl-4 border-l border-brand-moss/10 space-y-1 pb-2">
                {shopItems.map((item) => (
                  <NavLink
                    key={`m-${item.id}`}
                    to={item.to}
                    data-testid={`m-${item.id}`}
                    onClick={() => {
                      setOpen(false);
                      setMobileShopOpen(false);
                    }}
                    className="block py-2 text-sm text-brand-moss/75 hover:text-brand-moss"
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
            {topItems.map((item) => (
              <NavLink
                key={`m-${item.id}`}
                to={item.to}
                data-testid={`m-${item.id}`}
                onClick={() => setOpen(false)}
                className="text-brand-moss/80 hover:text-brand-moss py-3 text-base border-t border-brand-moss/5"
              >
                {item.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="m-nav-admin"
                onClick={() => setOpen(false)}
                className="text-brand-gold hover:text-brand-gold-light py-3 text-base border-t border-brand-moss/5 font-medium"
              >
                Admin
              </NavLink>
            )}
            <NavLink
              to={user ? "/dashboard" : "/login"}
              onClick={() => setOpen(false)}
              data-testid={user ? "m-nav-dashboard" : "m-nav-login"}
              className="text-brand-moss/80 hover:text-brand-moss py-3 text-base inline-flex items-center gap-2 border-t border-brand-moss/5"
            >
              <User className="w-4 h-4" /> {user ? "Dashboard" : "Sign in"}
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
}
